import { DOCUMENT } from '@angular/common';
import {
  inject,
  Injectable,
  InjectionToken,
  Renderer2,
  RendererFactory2,
  Signal,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { BUILD_ID } from '../../build-info';
import {
  ALLOWED_MANUFACTURERS,
  DEFAULT_MANUFACTURE_ALIAS,
  ManufactureType,
} from '../consts/manufactures.const';
import { DealerConfig } from '../interfaces';
import { dealerConfig } from '../state/dealer.config';

interface ThemeDefinition {
  bodyClass: string;
  cssFileHref?: string;
}

/**
 * Where ThemeService may write. Defaults target the document (standalone app).
 * A host can override this via THEME_HOST_CONFIG to redirect theming (e.g. into a
 * Shadow DOM) instead of the host page's document.
 */
export interface ThemeHostConfig {
  readonly brandHost: HTMLElement; // gets data-brand + brand-* body classes
  readonly varRoot: HTMLElement; // gets the --primary/--secondary API vars
  readonly styleHost: Node; // parent for the brand <link>
  readonly assetBase: string; // prefix for brand-*.css hrefs ('' = relative to base href)
  readonly syncThemeColor: boolean; // update <meta theme-color> (off when embedded)
  readonly urlSync: boolean; // auto-derive theme from the document URL (off when embedded)
}

export const THEME_HOST_CONFIG = new InjectionToken<ThemeHostConfig>('THEME_HOST_CONFIG', {
  providedIn: 'root',
  factory: (): ThemeHostConfig => {
    const doc: Document = inject(DOCUMENT);
    return {
      brandHost: doc.body,
      varRoot: doc.documentElement,
      styleHost: doc.head,
      assetBase: '',
      syncThemeColor: true,
      urlSync: true,
    };
  },
});

const MANUFACTUR_ALIASES: Record<string, ManufactureType> = {
  audi: 'audi',
  cupra: 'cupra',
  volkswagen: 'volkswagen',
  seat: 'seat',
  skoda: 'skoda',
  škoda: 'skoda',
  default: 'default',
};

/**
 * @desciption
 *  ThemeService is responsible for applying the correct theme to the application.
 *  Works in combination with angular.json and the brand-*.css files.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private readonly renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);
  // Optional so unit tests that don't configure RouterTestingModule still work.
  private readonly router: Router | null = inject(Router, { optional: true });
  // Where theming is written. Document by default; Shadow DOM when embedded.
  private readonly hostConfig: ThemeHostConfig = inject(THEME_HOST_CONFIG);
  // Tracked instead of getElementById so the brand <link> is also found inside a shadow root.
  private brandLink: HTMLLinkElement | null = null;

  private readonly knownBodyClasses = [
    'brand-audi',
    'brand-vw',
    'brand-seat',
    'brand-skoda',
    'brand-cupra',
  ];

  // Brand theming is intentionally gated on the URL path. Even if the URL
  // carries `?manufacturer=...` from an earlier step (e.g. /appointment/start,
  // /license-plate-*, /mileage-input), we don't activate the brand stylesheet
  // until the user is actually inside the vehicle-booking wizard. `/demo` and
  // `/live-demo` are exempt because they synthesise their own ?manufacturer to
  // preview brands without a full booking flow.
  private readonly brandThemePathPrefixes: readonly string[] = [
    '/appointment/plan-appointment/vehicle-booking',
    '/demo',
    '/live-demo',
  ];

  private readonly _themeReady = signal<boolean>(this.computeInitialThemeReady());
  public readonly themeReady: Signal<boolean> = this._themeReady.asReadonly();

  // The currently active manufacturer (or null when no brand theme is applied).
  // Header consumes this to decide whether to render a brand wordmark.
  private readonly _currentBrand = signal<ManufactureType | null>(null);
  public readonly currentBrand: Signal<ManufactureType | null> = this._currentBrand.asReadonly();

  private readonly themes: Record<ManufactureType, ThemeDefinition> = {
    audi: {
      bodyClass: 'brand-audi',
      cssFileHref: 'brand-audi.css',
    },
    volkswagen: {
      bodyClass: 'brand-vw',
      cssFileHref: 'brand-volkswagen.css',
    },
    seat: {
      bodyClass: 'brand-seat',
      cssFileHref: 'brand-seat.css',
    },
    cupra: {
      bodyClass: 'brand-cupra',
      cssFileHref: 'brand-cupra.css',
    },
    skoda: {
      bodyClass: 'brand-skoda',
      cssFileHref: 'brand-skoda.css',
    },
    default: {
      bodyClass: 'brand-default',
      cssFileHref: 'brand-volkswagen.css',
    },
  };
  public constructor() {
    // Defense-in-depth for the per-route themeGuard. The guard is only attached
    // to a subset of routes (booking/* and /demo); routes like /appointment/start,
    // /authenticate, /license-plate-*, /live-demo never run it. Without this
    // listener, navigating from a brand-styled page to one of those routes —
    // or refreshing on one of them with a stale ?manufacturer in the URL — would
    // leak the previous brand's body[data-brand] + stylesheet. This subscription
    // re-syncs the theme on every successful navigation, so the brand stylesheet
    // always reflects the current URL. Subscription lives for the app lifetime
    // because the service is providedIn: 'root'.
    // Embedded (web-component) runs an in-memory router: the document URL is the
    // host page's, not ours, so URL-driven theming would strip the brand on every
    // navigation. The embed drives theming explicitly via applyManufactureTheming.
    if (!this.hostConfig.urlSync) {
      return;
    }
    this.syncThemeFromUrl();
    // Optional chain both the router and its events stream: tests that provide
    // a Router stub without RouterTestingModule end up with `events` undefined,
    // which would otherwise throw `Cannot read properties of undefined (pipe)`.
    this.router?.events
      ?.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((): void => this.syncThemeFromUrl());
  }

  private isBrandThemePath(pathname: string): boolean {
    return this.brandThemePathPrefixes.some(
      (prefix: string): boolean => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  }

  private syncThemeFromUrl(): void {
    const view = this.document.defaultView;
    const pathname: string = view?.location?.pathname ?? '';

    if (!this.isBrandThemePath(pathname)) {
      this.removeManufactureTheming();
      return;
    }

    const search: string = view?.location?.search ?? '';
    const manufacturer: string | null = new URLSearchParams(search).get('manufacturer');
    if (manufacturer) {
      this.applyManufactureTheming(manufacturer as ManufactureType);
    } else {
      this.removeManufactureTheming();
    }
  }

  // Same brand gate as syncThemeFromUrl, but for a caller-supplied path + manufacturer instead of the
  // document URL. The web-component embed drives this from its in-memory router so pre-booking pages
  // stay default-themed (they aren't built for brand colours) and only the wizard gets the brand.
  public syncThemeForPath(pathname: string, manufacturer: string | null): void {
    if (!manufacturer || !this.isBrandThemePath(pathname)) {
      this.removeManufactureTheming();
      return;
    }
    this.applyManufactureTheming(manufacturer as ManufactureType);
  }

  public applyApiTheming(theme: DealerConfig): void {
    const root: HTMLElement = this.hostConfig.varRoot;

    if (theme.primaryButtonColor && theme.primaryButtonColor.trim()) {
      root.style.setProperty('--primary', theme.primaryButtonColor);
      root.style.setProperty('--primary-light', this.darkenHex(theme.primaryButtonColor));
    }

    if (theme.secondaryButtonColor && theme.secondaryButtonColor.trim()) {
      root.style.setProperty('--secondary', theme.secondaryButtonColor);
      root.style.setProperty('--secondary-light', this.darkenHex(theme.secondaryButtonColor));
    }
  }

  private darkenHex(hex: string, amount: number = 0.85): string {
    const clean = hex.replace('#', '');
    const r = Math.round(Number.parseInt(clean.slice(0, 2), 16) * amount);
    const g = Math.round(Number.parseInt(clean.slice(2, 4), 16) * amount);
    const b = Math.round(Number.parseInt(clean.slice(4, 6), 16) * amount);
    return `rgb(${r}, ${g}, ${b})`;
  }

  public applyManufactureTheming(brand: ManufactureType): void {
    const normalizedBrand: string = this.normalizeBrand(brand);
    if (!ALLOWED_MANUFACTURERS.includes(normalizedBrand)) {
      this.removeManufactureTheming();
      return;
    }

    const theme: ThemeDefinition = this.themes[normalizedBrand];
    const body: HTMLElement = this.hostConfig.brandHost;

    this.knownBodyClasses.forEach((className: string): void => {
      this.renderer.removeClass(body, className);
    });

    this.renderer.setAttribute(body, 'data-brand', normalizedBrand);
    this._currentBrand.set(normalizedBrand as ManufactureType);

    this.swapThemeStylesheet(this.withCacheBust(theme.cssFileHref));
  }

  // brand-*.css are inject:false bundles with stable names; a per-build ?v= busts the CDN cache.
  // assetBase is '' for the standalone app (resolves against <base href>) and an absolute origin
  // for the embed, whose <link> lives in a shadow root and can't rely on the host's base href.
  private withCacheBust(href: string | undefined): string {
    return href ? `${this.hostConfig.assetBase}${href}?v=${BUILD_ID}` : '';
  }

  public removeManufactureTheming(): void {
    const body: HTMLElement = this.hostConfig.brandHost;

    this.knownBodyClasses.forEach((className: string): void => {
      this.renderer.removeClass(body, className);
    });

    this.renderer.removeAttribute(body, 'data-brand');
    this._currentBrand.set(null);

    const config = dealerConfig();

    if (config != null) {
      this.applyApiTheming(config);
    }

    this.swapThemeStylesheet('');
  }

  private swapThemeStylesheet(href: string): void {
    if (!href) {
      this.brandLink?.remove();
      this.brandLink = null;
      this._themeReady.set(true);
      this.syncThemeColor();
      return;
    }

    if (this.brandLink && this.brandLink.getAttribute('href') === href) {
      this._themeReady.set(true);
      this.syncThemeColor();
      return;
    }

    if (!this.brandLink) {
      const link: HTMLLinkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'id', 'app-brand-theme');
      this.renderer.setAttribute(link, 'rel', 'stylesheet');
      this.renderer.appendChild(this.hostConfig.styleHost, link);
      this.brandLink = link;
    }

    this._themeReady.set(false);
    // Sync the iOS status-bar colour only once the brand CSS is in, so the computed
    // background reflects the new brand rather than the outgoing one.
    const onSettled = (): void => {
      this._themeReady.set(true);
      this.syncThemeColor();
    };
    this.brandLink.onload = onSettled;
    this.brandLink.onerror = onSettled;
    this.renderer.setAttribute(this.brandLink, 'href', href);
  }

  // Paints the iOS Safari status-bar / home-indicator bands in the active brand's page
  // background (`.body { background: var(--wp-background) }`) instead of the static
  // index.html default. Reads the *computed* colour so the brand CSS stays the single
  // source of truth — no per-brand hex duplicated here.
  private syncThemeColor(): void {
    // Embedded: the host owns <meta theme-color>; never mutate it (matches the iframe behaviour).
    if (!this.hostConfig.syncThemeColor) {
      return;
    }
    const view = this.document.defaultView;
    const body: HTMLElement | null = this.document.body;
    if (!view || !body) {
      return;
    }

    const background: string = view.getComputedStyle(body).backgroundColor;
    if (!background || background === 'transparent' || background === 'rgba(0, 0, 0, 0)') {
      return;
    }

    let meta = this.document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = this.renderer.createElement('meta');
      this.renderer.setAttribute(meta, 'name', 'theme-color');
      this.renderer.appendChild(this.document.head, meta);
    }
    this.renderer.setAttribute(meta, 'content', background);
  }

  private computeInitialThemeReady(): boolean {
    try {
      const view = this.document.defaultView;
      const pathname: string = view?.location?.pathname ?? '';
      if (!this.isBrandThemePath(pathname)) {
        return true;
      }
      const search: string = view?.location?.search ?? '';
      const manufacturer: string | null = new URLSearchParams(search).get('manufacturer');
      if (!manufacturer) {
        return true;
      }
      const normalized: ManufactureType = this.normalizeBrand(manufacturer);
      return !ALLOWED_MANUFACTURERS.includes(normalized);
    } catch {
      return true;
    }
  }

  public normalizeBrand(brand: string | null | undefined): ManufactureType {
    if (!brand?.trim()) {
      return DEFAULT_MANUFACTURE_ALIAS;
    }

    const normalizedBrand = brand.trim().toLocaleLowerCase();
    return MANUFACTUR_ALIASES[normalizedBrand] ?? DEFAULT_MANUFACTURE_ALIAS;
  }
}
